import { Stack, Typography } from '@mui/material';
import React from 'react';
import { useTranslation } from 'react-i18next';

import { TDynamicFormBuilderProps } from '@/console/features/dynamic-form/dynamic-form.types';
import { DynamicFieldRenderer } from '@/shared/components/ui/form/components/dynamic-field-renderer/dynamic-field-renderer';
import { TDynamicField } from '@/shared/components/ui/form/components/dynamic-field-renderer/dynamic-field-renderer.types';
import {
  FeatureConfigOptionType,
  InputTypeEnum,
} from '@/shared/types/types.generated';

export const DynamicForm: React.FC<TDynamicFormBuilderProps> = ({
  options,
  headline,
  formNamePrefix,
}) => {
  const { t } = useTranslation();

  const getLabelTranslation = (label: string) => t(`dynamic_form:${label}`);

  const getInputName = (value: string) =>
    formNamePrefix ? `${formNamePrefix}.${value}` : value;

  const toNormalizedField = (
    option: FeatureConfigOptionType,
  ): TDynamicField | null => {
    // Credential inputs render as vault-secret pickers outside the dynamic form
    if (option.inputType === InputTypeEnum.Credential) {
      return null;
    }

    const name = getInputName(option.value);
    const label = getLabelTranslation(option.label);
    const hint = getLabelTranslation(option.description);
    const shared = {
      name,
      label,
      required: option.required,
      defaultValue: option.defaultValue,
      hint,
    };

    if (option.inputType === InputTypeEnum.InputNumber) {
      return { ...shared, kind: 'number' };
    }

    if (option.inputType === InputTypeEnum.Switch) {
      const leftOption = option.inputOptions?.[0];
      const rightOption = option.inputOptions?.[1];

      return {
        ...shared,
        kind: 'toggle',
        toggleLeftLabel: leftOption
          ? getLabelTranslation(leftOption.label)
          : undefined,
        toggleRightLabel: rightOption
          ? getLabelTranslation(rightOption.label)
          : undefined,
      };
    }

    if (option.inputType === InputTypeEnum.RadioButton) {
      return {
        ...shared,
        kind: 'radio',
        options: (option.inputOptions ?? []).map((opt) => ({
          label: getLabelTranslation(opt.label),
          value: opt.value,
        })),
      };
    }

    if (option.inputType === InputTypeEnum.Select) {
      // Preserved as-is: unlike RadioButton, Select's options are NOT translated today.
      return { ...shared, kind: 'select', options: option.inputOptions ?? [] };
    }

    if (option.inputType === InputTypeEnum.InputText) {
      return { ...shared, kind: 'text' };
    }

    return null;
  };

  return (
    <Stack gap="1rem">
      {headline && <Typography variant="p6">{headline}</Typography>}
      {options.map((option) => {
        const field = toNormalizedField(option);

        return field ? (
          <DynamicFieldRenderer key={option.value} field={field} />
        ) : null;
      })}
    </Stack>
  );
};
